import { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { modifyApi } from '../../api/modify'
import type { ModifyEdit, ModifyPreviewRow } from '../../api/modify'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useModifyWizard } from '../../modify/ModifyWizardContext'
import { useAsync } from '../../hooks/useAsync'
import { formatInt } from '../../lib/format'
import { MODIFY_STEPS } from './steps'

/* Step 3, the screen that makes Modify worth having: the real matching records
   as an editable grid. Every cell is an input; a cell whose value differs from
   what QuickBooks returned is marked dirty, and Save sends only the dirty rows
   and only their dirty fields — everything else on the record is left alone.

   Save is a click handler, never an effect: an effect-on-mount submit queued two
   identical jobs under React StrictMode (that bug was fixed on the Delete flow
   this session). */

const PAGE_SIZE = 25

interface Column {
  name: string
  label: string
}

/**
 * When exportColumns is provided, use those (matching Export's template columns).
 * Otherwise fall back to the entity's non-line fields, plus any record keys they
 * don't cover.
 */
function buildColumns(
  exportColumns: string[] | null | undefined,
  fields: { name: string; label: string; isLineField: boolean }[],
  rows: ModifyPreviewRow[],
): Column[] {
  if (exportColumns && exportColumns.length > 0) {
    // Build a map from label to field name
    const labelToName = new Map<string, string>()
    for (const field of fields ?? []) {
      labelToName.set(field.label.toUpperCase(), field.name)
    }

    // Map export column labels to their actual field names
    return exportColumns.map((label) => ({
      name: labelToName.get(label.toUpperCase()) ?? label,
      label,
    }))
  }

  // Fallback: derive from entity fields
  const columns: Column[] = (fields ?? [])
    .filter((field) => !field.isLineField)
    .map((field) => ({ name: field.name, label: field.label }))

  const known = new Set(columns.map((column) => column.name))
  for (const record of rows) {
    for (const key of Object.keys(record.fields ?? {})) {
      if (!known.has(key)) {
        known.add(key)
        columns.push({ name: key, label: key })
      }
    }
  }

  return columns
}

function displayValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/**
 * The value a record holds for a column.
 *
 * The record bag is flat and keyed by path, not by property: a reference comes
 * back as `VendorRef.name` (and `VendorRef.value`), a memo as `CustomerMemo.value`,
 * an e-mail as `PrimaryEmailAddr.Address`. Looking up the bare field name found
 * nothing, so every reference column rendered empty even when its name resolved
 * correctly — Vendor, Terms and AP Account were blank on every row while the
 * record plainly held them.
 *
 * The suffixes mirror the export path's resolver, which reads the same bag and
 * had always handled this. The id is preferred over the display name only when
 * there is no name: a grid shows what the user recognises.
 */
const VALUE_SUFFIXES = ['.name', '.value', '.Address', '.FreeFormNumber']

function originalValue(row: ModifyPreviewRow, column: Column): string {
  const fields = row.fields ?? {}

  const direct = fields[column.name] ?? fields[column.name.toLowerCase()]
  if (direct != null) return displayValue(direct)

  for (const suffix of VALUE_SUFFIXES) {
    const nested = fields[`${column.name}${suffix}`]
    if (nested != null) return displayValue(nested)
  }

  return ''
}

/** Dirty edits keyed by row id, then by field name. */
type Draft = Record<string, Record<string, string>>

export function ModifyEditPage() {
  const wizard = useModifyWizard()
  const navigate = useNavigate()
  const { current } = useCompanies()
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState<Draft>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useAsync(
    () => {
      const companyId = current?.id
      if (!companyId || !wizard.entityType) return Promise.resolve(null)
      return modifyApi.preview({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        reference: wizard.reference || undefined,
        dateMode: wizard.dateMode,
        preset: wizard.dateMode === 'preset' ? wizard.preset : undefined,
        from: wizard.dateMode === 'custom' ? wizard.from || undefined : undefined,
        to: wizard.dateMode === 'custom' ? wizard.to || undefined : undefined,
        page,
        pageSize: PAGE_SIZE,
      })
    },
    [
      current?.id,
      wizard.entityType,
      wizard.reference,
      wizard.dateMode,
      wizard.preset,
      wizard.from,
      wizard.to,
      page,
    ],
  )

  // Stable references: `buildColumns` returns a fresh array every render, and
  // feeding that straight into `dirtyEdits` would recompute it constantly.
  const rows = useMemo(() => preview.data?.records ?? [], [preview.data])

  /* The full field list, not the import's narrowed one.
     `entity.fields` is the set this import can send, which is the right
     vocabulary for the Mapping screen and the wrong one here: this grid edits
     records QuickBooks already holds, so a field outside the request body — a
     bill's Terms, its Bill Due date — is still a column whose value the user
     needs to see and change. The narrowed list left those template labels
     resolving to nothing, and `buildColumns` then fell back to the label text
     itself, which matches no key in a record and rendered every such cell blank.
     `allFields` is absent only on an older API, where falling back keeps the old
     behaviour rather than emptying the grid. */
  const fieldVocabulary = wizard.entity?.allFields ?? wizard.entity?.fields ?? []
  const columns = useMemo(
    () => buildColumns(preview.data?.exportColumns, fieldVocabulary, rows),
    [preview.data?.exportColumns, fieldVocabulary, rows],
  )

  /** Every row+field whose typed value differs from QuickBooks' current value. */
  const dirtyEdits = useMemo<ModifyEdit[]>(() => {
    const edits: ModifyEdit[] = []
    for (const row of rows) {
      const rowDraft = draft[row.id]
      if (!rowDraft) continue
      const changed: Record<string, string | null> = {}
      for (const column of columns) {
        if (!(column.name in rowDraft)) continue
        const next = rowDraft[column.name]
        if (next !== originalValue(row, column)) changed[column.name] = next
      }
      if (Object.keys(changed).length > 0) {
        edits.push({ id: row.id, syncToken: row.syncToken, fields: changed })
      }
    }
    return edits
  }, [rows, columns, draft])

  const setCell = (rowId: string, field: string, value: string) =>
    setDraft((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [field]: value },
    }))

  const discard = () => setDraft({})

  /** Paging reloads the grid, so unsaved edits would be lost silently. */
  const goToPage = (next: number) => {
    if (dirtyEdits.length > 0 && !window.confirm('Discard unsaved changes and load another page?')) {
      return
    }
    setDraft({})
    setPage(next)
  }

  const save = async () => {
    const companyId = current?.id
    if (!companyId) {
      setError('No QuickBooks company is connected.')
      return
    }
    if (dirtyEdits.length === 0) return

    setSubmitting(true)
    setError(null)
    try {
      const { jobId } = await modifyApi.submit({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        edits: dirtyEdits,
      })
      navigate('/app/modify/done', { state: { jobId, count: dirtyEdits.length } })
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
      setSubmitting(false)
    }
  }

  if (!wizard.entityType) return <Navigate to="/app/modify" replace />

  const subject = wizard.entity?.label ?? wizard.entityType
  const count = preview.data?.totalCount
  const last = count == null ? 1 : Math.max(1, Math.ceil(count / PAGE_SIZE))
  const dirty = dirtyEdits.length

  return (
    <div className="bb bb-wizard bb-wizard-wide bb-modify-edit">
      {/* Match export preview: reduce vertical spacing and enable horizontal scrolling */}
      <style>{`
        .bb-modify-edit { padding-top: 0; padding-bottom: 8px }
        .bb-modify-edit .bb-steps { margin-bottom: 16px }
        .bb-modify-edit .bb-activity-head { margin-bottom: 8px }
      `}</style>

      <BbStepper steps={MODIFY_STEPS} current={2} />

      <div className="bb-activity-head">
        <div>
          <h2>Edit {subject}</h2>
          <p className="bb-map-help">
            Change any cell below. Only the cells you touch are sent to QuickBooks — everything else
            is left exactly as it is.
          </p>
        </div>
        <span className="bb-wizard-foot-r">
          <Link className="bb-btn bb-btn-quiet" to="/app/modify/filters">
            Previous
          </Link>
        </span>
      </div>

      {error ? (
        <Alert tone="err" title="Nothing has been saved" style={{ marginBottom: 18 }}>
          {error}
        </Alert>
      ) : null}

      {preview.error ? (
        <Alert tone="warn" title="Could not load records from QuickBooks">
          {preview.error.message}
        </Alert>
      ) : columns.length === 0 ? (
        <div className="bb-empty-ref">
          <BbIcon name="sheet" />
          <h3>No fields to edit</h3>
          <p>The API returned no fields for {subject}.</p>
        </div>
      ) : (
        <div className="bb-activity" style={{ marginTop: 20 }}>
          {/* Scrollable table container matching export preview */}
          <div
            className="bb-modify-edit-scroll"
            style={{
              overflow: 'auto',
              maxHeight: 'max(240px, calc(100dvh - 550px))',
            }}
          >
          <table className="bb-rtable" style={{ minWidth: 'max-content' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {columns.map((column) => (
                  <th key={column.name}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.loading ? (
                <tr>
                  <td colSpan={columns.length} className="bb-map-help">
                    Loading records from QuickBooks…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="bb-map-help">
                    No records match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => {
                      const original = originalValue(row, column)
                      const value = draft[row.id]?.[column.name] ?? original
                      const isDirty = value !== original
                      return (
                        <td key={column.name}>
                          <input
                            className={isDirty ? 'bb-input bb-cell bb-cell-dirty' : 'bb-input bb-cell'}
                            aria-label={`${column.label} for ${row.id}`}
                            value={value}
                            onChange={(event) => setCell(row.id, column.name, event.target.value)}
                          />
                        </td>
                      )
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          <div className="bb-pager">
            <span className="bb-pager-n">
              {count == null
                ? `${formatInt(rows.length)} record${rows.length === 1 ? '' : 's'}`
                : `${formatInt(count)} record${count === 1 ? '' : 's'} in QuickBooks`}
            </span>
            <span className="bb-pager-c">
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === 1}
                aria-label="First page"
                onClick={() => goToPage(1)}
              >
                |&lsaquo;
              </button>
              <button
                className="bb-pager-b"
                type="button"
                disabled={page === 1}
                aria-label="Previous page"
                onClick={() => goToPage(page - 1)}
              >
                &lsaquo;
              </button>
              <span className="bb-pager-b bb-pager-on" aria-current="page">
                {page}
              </span>
              <button
                className="bb-pager-b"
                type="button"
                disabled={page >= last}
                aria-label="Next page"
                onClick={() => goToPage(page + 1)}
              >
                &rsaquo;
              </button>
              <button
                className="bb-pager-b"
                type="button"
                disabled={page >= last}
                aria-label="Last page"
                onClick={() => goToPage(last)}
              >
                &rsaquo;|
              </button>
            </span>
          </div>
        </div>
      )}

      <div className="bb-wizard-foot">
        <button
          className="bb-btn bb-btn-quiet"
          type="button"
          disabled={dirty === 0}
          onClick={discard}
        >
          Discard changes
        </button>
        <span className="bb-wizard-foot-r">
          <span className="bb-map-help" style={{ marginRight: 12 }}>
            {dirty === 0
              ? 'No changes yet'
              : `${formatInt(dirty)} record${dirty === 1 ? '' : 's'} changed`}
          </span>
          <button
            className="bb-btn bb-btn-green"
            type="button"
            disabled={dirty === 0 || submitting}
            onClick={() => void save()}
          >
            <BbIcon name="save" />
            {submitting ? 'Saving…' : 'Save to QuickBooks'}
          </button>
        </span>
      </div>
    </div>
  )
}
